import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import {
  CalendarMonthOutlined as YearIcon,
  CompareArrowsOutlined as CompareIcon,
  StraightenOutlined as MeasureIcon,
  FileDownloadOutlined as DownloadIcon,
  UploadFileOutlined as ImportIcon,
} from '@mui/icons-material';
import PayrollService, { VolumeWeek, describeError } from '../../api/services/payroll.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import StatTile from '../../components/ui/StatTile';
import { ChoiceFilter, FilterBar } from '../../components/ui/FilterBar';
import {
  GROUP_ROW_HEIGHT, HEAD_ROW_HEIGHT,
} from '../../components/ui/tableStyle';
import { EmptyState, LoadingState } from '../../components/ui/States';
import { toCsv, downloadCsv } from '../../utils/csv';
import ReportsPanel from '../reports/ReportsPanel';
import VolumeImportDialog from './VolumeImportDialog';
import { LineChart, LineWeek } from './reportCharts';
import { Delta, count, money, shortDate } from './payrollUI';
import {
  change, packagesPerStop, perStop, sameWeekIn, total, weeksOfYear, yearToDate, yearsOf,
} from './volumeMath';

// Weekly volume: the family's "#PKG vs Stops" sheet, kept by itself.
//
// One row a week — packages and stops by kind, revenue, revenue per stop —
// each against the same week of another year. Every uploaded statement
// fills its week; the years before TopoSDS are imported once from the
// sheet. A download gives the same table back as a spreadsheet.

type Metric = 'totalStops' | 'totalPackages' | 'revenue';
const METRIC_LABEL: Record<Metric, string> = { totalStops: 'Stops', totalPackages: 'Packages', revenue: 'Revenue' };

const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

// Rows are one line (36px), so a year of weeks fits on a screen: the
// change sits beside its number, not under it.
const ROW = 36;

// A change beside its number: arrow and percent to two places, as the
// sheet has it, green when up. The arrow carries it as well as the colour.
const Change: React.FC<{ value: number | null }> = ({ value }) => {
  if (value === null) return null;
  const up = value > 0;
  const flat = Math.abs(value) < 0.005;
  return (
    <Typography
      component="span" variant="caption"
      sx={{ ...num, ml: 0.75, display: 'inline-block', minWidth: 58, textAlign: 'left', fontWeight: 600, color: flat ? 'text.secondary' : up ? 'success.dark' : 'error.dark' }}
    >
      {flat ? '0.00%' : `${up ? '▴' : '▾'} ${Math.abs(value).toFixed(2)}%`}
    </Typography>
  );
};

// One set of column widths for both tables below — the weeks and the
// totals — so the totals line up under their columns exactly.
const COLS = ['9%', '6.5%', '6.5%', '6.5%', '6.5%', '6.5%', '6.5%', '11%', '11%', '13.5%', '8.5%', '7.5%'];
const ColGroup = () => <colgroup>{COLS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>;

const Cell: React.FC<{ children: React.ReactNode; strong?: boolean; divider?: boolean }> = ({ children, strong, divider }) => (
  <TableCell
    align="right"
    sx={{ ...num, py: 0, px: 1, fontWeight: strong ? 700 : 400, ...(divider ? { borderLeft: '1px solid', borderLeftColor: 'divider' } : {}) }}
  >
    {children}
  </TableCell>
);

const WeeklyVolume: React.FC = () => {
  const [weeks, setWeeks] = useState<VolumeWeek[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState<number | null>(null);
  const [against, setAgainst] = useState<number | null>(null);
  const [metric, setMetric] = useState<Metric>('totalStops');
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const all = await PayrollService.volume();
      setWeeks(all);
      setError(null);
    } catch (err) {
      setError(describeError(err, 'Could not load the weekly volume.'));
      setWeeks([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const years = useMemo(() => yearsOf(weeks || []), [weeks]);
  // The newest year, against the one before it — the sheet's default view.
  useEffect(() => {
    if (!years.length) return;
    if (year === null || !years.includes(year)) setYear(years[0]);
  }, [years, year]);
  useEffect(() => {
    if (year === null) return;
    if (against === null || against === year) setAgainst(years.includes(year - 1) ? year - 1 : years.find((y) => y !== year) ?? null);
  }, [year, years, against]);

  const rows = useMemo(() => (weeks && year !== null
    ? weeksOfYear(weeks, year).map((w) => ({ w, was: against === null ? null : sameWeekIn(weeks, w, against) }))
    : []), [weeks, year, against]);
  const ytd = useMemo(() => (weeks && year !== null && against !== null ? yearToDate(weeks, year, against) : null), [weeks, year, against]);
  const sum = useMemo(() => total(rows.map((r) => r.w)), [rows]);

  // The chart's weeks: this year's, then the rest of the other year's on
  // the same calendar, so a part-year shows where last year went next.
  const chart = useMemo(() => {
    if (!weeks || year === null) return null;
    const slots: { label: string; key: string; now: VolumeWeek | null; was: VolumeWeek | null }[] = rows.map((r) => ({
      key: r.w.weekEnding, label: shortDate(r.w.weekEnding), now: r.w, was: r.was,
    }));
    if (against !== null) {
      const lastShown = rows.length ? new Date(rows[rows.length - 1].w.weekEnding) : null;
      for (const w of weeksOfYear(weeks, against)) {
        const d = new Date(w.weekEnding);
        const shifted: Date = new Date(Date.UTC(year, d.getUTCMonth(), d.getUTCDate()));
        if (lastShown && shifted.getTime() <= lastShown.getTime() + 3 * 86400000) continue;
        if (shifted.getUTCFullYear() !== year) continue;
        slots.push({ key: `was-${w.weekEnding}`, label: shortDate(shifted.toISOString()), now: null, was: w });
      }
    }
    return slots;
  }, [weeks, year, against, rows]);

  const download = () => {
    if (year === null) return;
    const pct = (v: number | null) => (v === null ? '' : `${v.toFixed(2)}%`);
    const lines = rows.map(({ w, was }) => [
      shortDate(w.weekEnding) + `, ${year}`,
      w.pickupPackages, w.pickupStops, w.deliveryPackages, w.deliveryStops, w.ecommercePackages, w.ecommerceStops,
      w.totalPackages, pct(change(w.totalPackages, was?.totalPackages)),
      w.totalStops, pct(change(w.totalStops, was?.totalStops)),
      w.revenue ?? '', pct(change(w.revenue, was?.revenue)),
      perStop(w) ?? '', packagesPerStop(w) ?? '',
    ]);
    const vs = against === null ? '' : ` vs ${against}`;
    downloadCsv(`weekly-volume-${year}.csv`, toCsv([
      'Week ending', 'Pick-up #PKG', 'Pick-up Stops', 'Delivery #PKG', 'Delivery Stops', 'E-Com #PKG', 'E-Com Stops',
      'TOTAL #PKG', `#PKG${vs}`, 'TOTAL Stops', `Stops${vs}`, 'Revenue', `Revenue${vs}`, '$ per stop', '#PKG per stop',
    ], lines));
  };

  const defaultYear = years[0] ?? null;
  const defaultAgainst = defaultYear === null ? null : (years.includes(defaultYear - 1) ? defaultYear - 1 : years.find((y) => y !== defaultYear) ?? null);
  const filtered = year !== defaultYear || against !== defaultAgainst || metric !== 'totalStops';
  const reset = () => { setYear(defaultYear); setAgainst(defaultAgainst); setMetric('totalStops'); };
  const NONE = -1;

  const filters = years.length > 0 && year !== null && (
    <FilterBar onReset={reset} canReset={filtered}>
      <ChoiceFilter<number>
        icon={<YearIcon />}
        label="Year"
        value={year}
        defaultValue={defaultYear ?? year}
        onChange={setYear}
        choices={years.map((y) => ({ key: y, label: String(y) }))}
      />
      <ChoiceFilter<number>
        icon={<CompareIcon />}
        label="Compared with"
        value={against ?? NONE}
        defaultValue={defaultAgainst ?? NONE}
        onChange={(y) => setAgainst(y === NONE ? null : y)}
        choices={[...years.filter((y) => y !== year).map((y) => ({ key: y, label: String(y) })), { key: NONE, label: 'Nothing' }]}
      />
      <ChoiceFilter<Metric>
        icon={<MeasureIcon />}
        label="Chart"
        value={metric}
        defaultValue="totalStops"
        onChange={setMetric}
        choices={(Object.keys(METRIC_LABEL) as Metric[]).map((m) => ({ key: m, label: METRIC_LABEL[m] }))}
      />
    </FilterBar>
  );
  const actions = years.length > 0 && (
    <>
      <Button size="small" startIcon={<ImportIcon />} onClick={() => setImporting(true)}>Import past weeks</Button>
      <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={download}>Download</Button>
    </>
  );

  const vs = against === null ? undefined : `${against}`;
  const fmtMetric = (n: number) => (metric === 'revenue' ? money(Math.round(n)) : count(Math.round(n)));

  return (
    <PageBody fluid>
      <ReportsPanel />
      <PageHeader title="Weekly volume" actions={actions}>{filters}</PageHeader>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {weeks === null ? <LoadingState /> : !weeks.length ? (
        <EmptyState
          title="No weeks yet"
          description="Each statement you upload adds its week here. Past years can come in from your own sheet."
          action={{ label: 'Import past weeks', icon: <ImportIcon />, onClick: () => setImporting(true) }}
        />
      ) : (
        <Stack gap={2}>
          {ytd && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              <StatTile
                label={`Stops, ${year}`} value={count(ytd.now.totalStops)} hint={`${ytd.weeks} weeks`}
                trend={ytd.paired > 0 && <Delta diff={ytd.pairedNow.totalStops - ytd.pairedWas.totalStops} better="up" format={(n) => count(n)} against={`same weeks of ${vs}`} />}
              />
              <StatTile
                label={`Packages, ${year}`} value={count(ytd.now.totalPackages)} hint={`${packagesPerStop(ytd.now) ?? '—'} per stop`}
                trend={ytd.paired > 0 && <Delta diff={ytd.pairedNow.totalPackages - ytd.pairedWas.totalPackages} better="up" format={(n) => count(n)} against={`same weeks of ${vs}`} />}
              />
              <StatTile
                label={`Revenue, ${year}`} value={money(Math.round(ytd.now.revenue))} hint={`${money(Math.round(ytd.now.revenue / Math.max(1, ytd.weeks)))} a week`}
                trend={ytd.paired > 0 && <Delta diff={ytd.pairedNow.revenue - ytd.pairedWas.revenue} better="up" format={(n) => money(Math.round(n))} against={`same weeks of ${vs}`} />}
              />
              <StatTile
                label="Revenue per stop" value={perStop(ytd.now) === null ? null : money(perStop(ytd.now), { cents: true })}
                trend={ytd.paired > 0 && perStop(ytd.pairedWas) !== null && (
                  <Delta diff={(perStop(ytd.pairedNow) ?? 0) - (perStop(ytd.pairedWas) ?? 0)} better="up" format={(n) => money(n, { cents: true })} against={`same weeks of ${vs}`} />
                )}
              />
            </Box>
          )}

          {chart && chart.length > 1 && (
              <LineChart
                title={`${METRIC_LABEL[metric]} a week`}
                weeks={chart.map((c): LineWeek => ({ key: c.key, label: c.label }))}
                series={[
                  { series: 'thisYear', label: String(year), values: chart.map((c) => (c.now ? (c.now[metric] as number | null) : null)) },
                  ...(against === null ? [] : [{ series: 'lastYear' as const, label: String(against), values: chart.map((c) => (c.was ? (c.was[metric] as number | null) : null)) }]),
                ]}
                format={fmtMetric}
                height={230}
                slotName={(w) => `Week ending ${w.label}`}
              />
          )}

          {/* The weeks scroll; the totals sit under them in a table of their
              own with the same columns. They used to be pinned to the bottom
              of the scroll box, which sliced the last visible week in half
              and made Safari repaint them on every scroll. Both tables share
              one sideways scroller, so on a narrow screen they move together. */}
          <Card sx={{ overflow: 'hidden' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ minWidth: 1180 }}>
                <TableContainer sx={{ maxHeight: '65vh', overflowY: 'auto', scrollbarGutter: 'stable' }}>
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
              <ColGroup />
              <TableHead>
                {/* The site's two header rows (components/ui/tableStyle): a
                    group row, then one-line column names; a name too long for
                    its column is short, with the full name on hover. */}
                <TableRow>
                  <TableCell rowSpan={2} sx={{ px: 1.25, py: 0, verticalAlign: 'bottom', height: GROUP_ROW_HEIGHT + HEAD_ROW_HEIGHT, pb: 1.25 }}>Week ending</TableCell>
                  {['Pick-up', 'Delivery', 'E-com'].map((g) => (
                    <TableCell key={g} colSpan={2} align="center" sx={{ py: 0, height: GROUP_ROW_HEIGHT, borderLeft: '1px solid', borderLeftColor: 'divider' }}>{g}</TableCell>
                  ))}
                  <TableCell colSpan={5} align="center" sx={{ py: 0, height: GROUP_ROW_HEIGHT, borderLeft: '1px solid', borderLeftColor: 'divider' }}>
                    Total{vs ? ` · change vs ${vs}` : ''}
                  </TableCell>
                </TableRow>
                <TableRow>
                  {[
                    ['Pkgs', 'Packages'], ['Stops'], ['Pkgs', 'Packages'], ['Stops'], ['Pkgs', 'Packages'], ['Stops'],
                    ['Packages'], ['Stops'], ['Revenue'], ['$ / stop', 'Revenue per stop'], ['Pkg / stop', 'Packages per stop'],
                  ].map(([h, full], i) => (
                    <TableCell
                      key={`${h}${i}`} align="right"
                      sx={{ top: GROUP_ROW_HEIGHT, height: HEAD_ROW_HEIGHT, py: 0, px: 1.25, ...(i % 2 === 0 && i < 8 ? { borderLeft: '1px solid', borderLeftColor: 'divider' } : {}) }}
                    >
                      {full ? <Tooltip title={full}><span>{h}</span></Tooltip> : h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(({ w, was }) => (
                  <TableRow key={w.weekEnding} hover sx={{ height: ROW }}>
                    <TableCell sx={{ ...num, py: 0, px: 1 }}>
                      <Tooltip title={w.source === 'statement' ? 'From the uploaded statement' : 'From the imported sheet'}>
                        <span>
                          {shortDate(w.weekEnding)}
                          {w.source === 'history' && <Box component="span" sx={{ color: 'text.disabled', ml: 0.5 }}>·</Box>}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <Cell divider>{count(w.pickupPackages)}</Cell>
                    <Cell>{count(w.pickupStops)}</Cell>
                    <Cell divider>{count(w.deliveryPackages)}</Cell>
                    <Cell>{count(w.deliveryStops)}</Cell>
                    <Cell divider>{count(w.ecommercePackages)}</Cell>
                    <Cell>{count(w.ecommerceStops)}</Cell>
                    <Cell divider strong>{count(w.totalPackages)}<Change value={change(w.totalPackages, was?.totalPackages)} /></Cell>
                    <Cell strong>{count(w.totalStops)}<Change value={change(w.totalStops, was?.totalStops)} /></Cell>
                    <Cell strong>{money(w.revenue, { cents: true })}<Change value={change(w.revenue, was?.revenue)} /></Cell>
                    <Cell>{perStop(w) === null ? '—' : money(perStop(w), { cents: true })}</Cell>
                    <Cell>{packagesPerStop(w)?.toFixed(2) ?? '—'}</Cell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
                </TableContainer>
                {rows.length > 0 && (
                  <Box sx={{ overflow: 'hidden', scrollbarGutter: 'stable', borderTop: '2px solid', borderColor: 'divider' }}>
                    <Table size="small" sx={{ tableLayout: 'fixed', '& td': { height: ROW, py: 0, color: 'text.primary' }, '& tr:last-of-type td': { borderBottom: 0 } }}>
                      <ColGroup />
                      <TableBody>
                        {[
                          { label: 'Total', v: sum, n: 1 },
                          { label: 'Weekly average', v: sum, n: rows.length },
                        ].map(({ label, v, n }) => (
                          <TableRow key={label}>
                            <TableCell sx={{ fontWeight: 700, px: 1 }}>{label}</TableCell>
                            <Cell divider>{count(Math.round(v.pickupPackages / n))}</Cell>
                            <Cell>{count(Math.round(v.pickupStops / n))}</Cell>
                            <Cell divider>{count(Math.round(v.deliveryPackages / n))}</Cell>
                            <Cell>{count(Math.round(v.deliveryStops / n))}</Cell>
                            <Cell divider>{count(Math.round(v.ecommercePackages / n))}</Cell>
                            <Cell>{count(Math.round(v.ecommerceStops / n))}</Cell>
                            <Cell divider strong>{count(Math.round(v.totalPackages / n))}</Cell>
                            <Cell strong>{count(Math.round(v.totalStops / n))}</Cell>
                            <Cell strong>{money(Math.round((v.revenue / n) * 100) / 100, { cents: true })}</Cell>
                            <Cell>{perStop(v) === null ? '—' : money(perStop(v), { cents: true })}</Cell>
                            <Cell>{packagesPerStop(v)?.toFixed(2) ?? '—'}</Cell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Stack>
      )}

      <VolumeImportDialog open={importing} onClose={() => setImporting(false)} onImported={load} />
    </PageBody>
  );
};

export default WeeklyVolume;
